import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader.js';

const API = '/api/door-drawings';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function folderPath(folder1, folder2, folder3, f3Opts) {
  let fp = folder1;
  if (folder2) fp += '/' + folder2;
  if (f3Opts.length > 0 && folder3) fp += '/' + folder3;
  else if (f3Opts.length === 0 && folder2) fp += '/' + folder2;
  return fp;
}

// Flatten tree into all possible destination folder paths for the move dropdown
function flattenTree(tree, prefix = '') {
  const results = [];
  if (!tree) return results;
  for (const key of Object.keys(tree)) {
    const full = prefix ? `${prefix}/${key}` : key;
    results.push(full);
    if (tree[key] && typeof tree[key] === 'object' && !tree[key].path) {
      results.push(...flattenTree(tree[key], full));
    }
  }
  return results;
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ background: '#fff', border: '2px solid #000', borderRadius: '8px', padding: '24px', width: '380px', boxShadow: '4px 4px 0 #000' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: '2px solid #000', borderRadius: '4px', fontSize: '18px', cursor: 'pointer', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, type, children, ...props }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      {label && <div style={{ fontSize: '11px', fontWeight: '700', color: '#333', marginBottom: '4px', textTransform: 'uppercase' }}>{label}</div>}
      {hint && <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{hint}</div>}
      {type === 'select' ? (
        <select {...props} style={{ width: '100%', padding: '9px 11px', border: '2px solid #000', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', background: '#fff', ...props.style }}>
          {children}
        </select>
      ) : (
        <input {...props} style={{ width: '100%', padding: '9px 11px', border: '2px solid #000', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', ...props.style }} />
      )}
    </div>
  );
}

function Btn({ children, variant, onClick, disabled }) {
  const bg = variant === 'danger' ? '#dc2626' : variant === 'primary' ? '#0070f3' : '#6b7280';
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '8px 18px', background: disabled ? '#ccc' : bg, color: '#fff',
      border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: disabled ? 'default' : 'pointer',
    }}>
      {children}
    </button>
  );
}

// ─── FolderRow — folder selector with rename/delete buttons ───────────────────

function FolderRow({ label, value, options, onChange, onMkdir, onRename, onDelete, showActions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
      <div style={{ flex: '1' }}>
        <div style={{ fontSize: '10px', fontWeight: '700', color: '#333', marginBottom: '3px', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <select value={value} onChange={e => onChange(e.target.value)}
            style={{ padding: '8px 10px', border: '2px solid #000', borderRadius: '6px', fontSize: '13px', background: '#fff', minWidth: '180px', flex: '1' }}>
            <option value="">— None —</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {showActions && value && (
            <>
              <button onClick={() => onMkdir(label, value)} title="New subfolder here"
                style={{ padding: '7px 10px', background: '#fff', border: '2px solid #000', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                + Sub
              </button>
              <button onClick={() => onRename(label, value)} title="Rename folder"
                style={{ padding: '7px 10px', background: '#fff', border: '2px solid #000', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
                ✏️
              </button>
              <button onClick={() => onDelete(label, value)} title="Delete folder"
                style={{ padding: '7px 10px', background: '#fff', border: '2px solid #dc2626', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', color: '#dc2626' }}>
                🗑
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DoorDrawingLibrary() {
  const navigate = useNavigate();

  // ── Folder state ────────────────────────────────────────────────────────────
  const [tree, setTree] = useState(null);
  const [folder1, setFolder1] = useState('');
  const [folder2, setFolder2] = useState('');
  const [folder3, setFolder3] = useState('');
  const [f1Opts, setF1Opts] = useState([]);
  const [f2Opts, setF2Opts] = useState([]);
  const [f3Opts, setF3Opts] = useState([]);

  // ── Upload state ────────────────────────────────────────────────────────────
  const [model, setModel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  // ── Selector state ─────────────────────────────────────────────────────────
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [svg, setSvg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(100);

  // ── Modal state ─────────────────────────────────────────────────────────────
  const [modal, setModal] = useState(null);   // 'mkdir' | 'rename' | 'movedest' | 'deletefile' | 'deletefolder'
  const [modalMode, setModalMode] = useState('');  // 'folder1' | 'folder2' | 'folder3' — which level
  const [modalValue, setModalValue] = useState('');
  const [modalDest, setModalDest] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // ── Load tree on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/tree`).then(r => r.json()).then(d => setTree(d.tree || {})).catch(() => {});
  }, []);

  // ── Folder cascade ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!tree) return;
    const top = Object.keys(tree);
    setF1Opts(top);
  }, [tree]);

  useEffect(() => {
    if (!folder1 || !tree) { setFolder2(''); setF2Opts([]); return; }
    const n = tree[folder1];
    const o = (n && typeof n === 'object' && !n.path) ? Object.keys(n) : [];
    setF2Opts(o);
    if (o.length > 0 && !o.includes(folder2)) setFolder2(o[0] || '');
    else setFolder2('');
  }, [folder1]);

  useEffect(() => {
    if (!folder1 || !folder2 || !tree) { setFolder3(''); setF3Opts([]); return; }
    const n = tree[folder1]?.[folder2];
    const o = (n && typeof n === 'object' && !n.path) ? Object.keys(n) : [];
    setF3Opts(o);
    if (o.length > 0 && !o.includes(folder3)) setFolder3(o[0] || '');
    else setFolder3('');
  }, [folder1, folder2]);

  // ── Load file list ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!folder1) return;
    const fp = folderPath(folder1, folder2, folder3, f3Opts);
    setLoading(true);
    setSelected(null); setSvg(null);
    fetch(`${API}/files?path=${encodeURIComponent(fp)}`)
      .then(r => r.json())
      .then(d => { setFiles(d.files || []); setLoading(false); })
      .catch(() => { setFiles([]); setLoading(false); });
  }, [folder1, folder2, folder3]);

  // ── Preview ──────────────────────────────────────────────────────────────────
  async function loadPreview(name) {
    const fp = folderPath(folder1, folder2, folder3, f3Opts);
    setSelected(name);
    setZoom(100);
    try {
      const res = await fetch(`${API}/drawing?path=${encodeURIComponent(fp + '/' + name)}`);
      const data = await res.json();
      setSvg(data.svg || null);
    } catch { setSvg(null); }
  }

  function refresh() {
    fetch(`${API}/tree`).then(r => r.json()).then(d => setTree(d.tree || {})).catch(() => {});
    if (folder1) {
      const fp = folderPath(folder1, folder2, folder3, f3Opts);
      fetch(`${API}/files?path=${encodeURIComponent(fp)}`)
        .then(r => r.json())
        .then(d => setFiles(d.files || []))
        .catch(() => {});
    }
  }

  // ── Folder actions triggered from FolderRow ─────────────────────────────────

  // Mkdir at a specific level
  function doMkdirAt() {
    // modalMode = 'folder1' | 'folder2' | 'folder3' | 'root'
    // parentPath = folder we're creating inside
    let parentPath = '';
    if (modalMode === 'folder1') parentPath = folder1;
    else if (modalMode === 'folder2') parentPath = folder1 + '/' + folder2;
    else if (modalMode === 'folder3') parentPath = folder1 + '/' + folder2 + '/' + folder3;
    else if (modalMode === 'root') parentPath = '';

    setModalLoading(true); setModalError('');
    fetch(`${API}/mkdir`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: parentPath, name: modalValue.trim() }),
    }).then(r => r.json()).then(d => {
      if (d.success) { setModal(null); refresh(); }
      else setModalError(d.error || 'Failed');
    }).catch(e => setModalError(e.message)).finally(() => setModalLoading(false));
  }

  // Rename folder at a specific level
  function doRenameFolder() {
    let oldPath = '';
    if (modalMode === 'folder1') oldPath = modalValue;
    else if (modalMode === 'folder2') oldPath = folder1 + '/' + modalValue;
    else if (modalMode === 'folder3') oldPath = folder1 + '/' + folder2 + '/' + modalValue;

    const newName = modalDest.trim();
    const parts = oldPath.split('/');
    parts.pop();
    parts.push(newName);
    const dest = parts.join('/');

    setModalLoading(true); setModalError('');
    fetch(`${API}/move`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ src: oldPath, dest }),
    }).then(r => r.json()).then(d => {
      if (d.success) {
        setModal(null);
        // Reset folder state to reflect renamed folder
        if (modalMode === 'folder1') setFolder1(newName);
        else if (modalMode === 'folder2') setFolder2(newName);
        else if (modalMode === 'folder3') setFolder3(newName);
        refresh();
      } else setModalError(d.error || 'Rename failed');
    }).catch(e => setModalError(e.message)).finally(() => setModalLoading(false));
  }

  // Delete folder at a specific level
  function doDeleteFolder() {
    let delPath = '';
    if (modalMode === 'folder1') delPath = modalValue;
    else if (modalMode === 'folder2') delPath = folder1 + '/' + modalValue;
    else if (modalMode === 'folder3') delPath = folder1 + '/' + folder2 + '/' + modalValue;

    setModalLoading(true); setModalError('');
    fetch(`${API}/folder?path=${encodeURIComponent(delPath)}`, { method: 'DELETE' })
      .then(r => r.json()).then(d => {
        if (d.success) {
          setModal(null);
          if (modalMode === 'folder1') { setFolder1(''); setFolder2(''); setFolder3(''); }
          else if (modalMode === 'folder2') { setFolder2(''); setFolder3(''); }
          else if (modalMode === 'folder3') setFolder3('');
          refresh();
        } else setModalError(d.error || 'Delete failed');
      }).catch(e => setModalError(e.message)).finally(() => setModalLoading(false));
  }

  // ── Upload ───────────────────────────────────────────────────────────────────
  async function handleUpload() {
    if (!selectedFile) { setUploadError('Select a file first'); return; }
    if (!model.trim()) { setUploadError('Enter a model name first'); return; }
    if (!folder1) { setUploadError('Select a folder first using the dropdowns above'); return; }
    setUploading(true); setUploadError(''); setUploadResult('');
    const fp = folderPath(folder1, folder2, folder3, f3Opts);
    const isDxf = selectedFile.name.toLowerCase().endsWith('.dxf');
    const isSvg = selectedFile.name.toLowerCase().endsWith('.svg');
    try {
      if (isSvg) {
        const text = await selectedFile.text();
        const r = await fetch(`${API}/upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_path: `${fp}/${model.trim()}.svg`, svg_content: text }),
        });
        const d = await r.json();
        if (d.success) {
          setUploadResult(`✅ Uploaded — ${fp}/${model.trim()}.svg`);
          setModel(''); setSelectedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          refresh();
        } else setUploadError(d.error || 'Upload failed');
      } else if (isDxf) {
        const reader = new FileReader();
        reader.onload = () => {
          const b64 = reader.result.split(',')[1];
          fetch(`${API}/drawing`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ folder: fp, filename: model.trim() + '.dxf', dxfData: b64 }),
          }).then(r => r.json()).then(d => {
            if (d.success) {
              setUploadResult(`✅ Uploaded — ${fp}/${model.trim()}.svg`);
              setModel(''); setSelectedFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
              refresh();
            } else setUploadError(d.error || 'Upload failed');
            setUploading(false);
          }).catch(() => { setUploadError('Upload failed'); setUploading(false); });
        };
        reader.readAsDataURL(selectedFile);
        return;
      } else {
        setUploadError('DXF or SVG only');
        setUploading(false);
      }
    } catch (e) { setUploadError('Upload failed: ' + e.message); setUploading(false); }
  }

  function handleDrop(e) {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const ok = file.name.toLowerCase().endsWith('.dxf') || file.name.toLowerCase().endsWith('.svg');
    if (!ok) { setUploadError('DXF or SVG only'); return; }
    setSelectedFile(file);
    if (!model.trim()) setModel(file.name.replace(/\.(dxf|svg)$/i, ''));
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    if (!model.trim()) setModel(file.name.replace(/\.(dxf|svg)$/i, ''));
  }

  // ── File actions ────────────────────────────────────────────────────────────
  function openRenameFile(filename) {
    setModal('renamefile');
    setModalValue(filename.replace('.svg', ''));
    setModalError('');
  }

  async function doRenameFile() {
    if (!modalValue.trim()) return;
    const fp = folderPath(folder1, folder2, folder3, f3Opts);
    const src = fp + '/' + selected;
    const dest = fp + '/' + modalValue.trim() + '.svg';
    setModalLoading(true); setModalError('');
    try {
      const r = await fetch(`${API}/move`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ src, dest }),
      });
      const d = await r.json();
      if (d.success) { setModal(null); setSelected(null); refresh(); }
      else setModalError(d.error || 'Rename failed');
    } catch (e) { setModalError(e.message); }
    setModalLoading(false);
  }

  function openMoveFile(filename) {
    setModal('movefile');
    setModalValue(filename);
    setModalDest(''); // let user type the full destination path
    setModalError('');
  }

  async function doMoveFile() {
    if (!modalDest.trim()) return;
    const fp = folderPath(folder1, folder2, folder3, f3Opts);
    const src = fp + '/' + modalValue;
    const dest = (modalDest.startsWith('/') ? modalDest.slice(1) : modalDest) + '/' + modalValue;
    setModalLoading(true); setModalError('');
    try {
      const r = await fetch(`${API}/move`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ src, dest }),
      });
      const d = await r.json();
      if (d.success) { setModal(null); setSelected(null); refresh(); }
      else setModalError(d.error || 'Move failed');
    } catch (e) { setModalError(e.message); }
    setModalLoading(false);
  }

  function confirmDeleteFile(filename) {
    setModal('deletefile');
    setModalValue(filename);
  }

  async function doDeleteFile() {
    if (!modalValue) return;
    const fp = folderPath(folder1, folder2, folder3, f3Opts);
    const path = fp + '/' + modalValue;
    try {
      const r = await fetch(`${API}/drawing?path=${encodeURIComponent(path)}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) { setModal(null); setSelected(null); refresh(); }
      else setModalError(d.error || 'Delete failed');
    } catch (e) { setModalError(e.message); }
  }

  const fp = folderPath(folder1, folder2, folder3, f3Opts);

  // ─── Modal helpers ──────────────────────────────────────────────────────────
  function openMkdirRoot() { setModal('mkdir'); setModalMode('root'); setModalValue(''); setModalError(''); }
  function openMkdirAt(label, currentValue) {
    const labelMap = { 'Files & Folders': 'folder1', 'Door Type': 'folder2', 'Operation Type': 'folder3' };
    setModalMode(labelMap[label] || label);
    setModalValue(''); setModalError('');
    setModal('mkdir');
  }
  function openRenameFolder(label, currentName) {
    const labelMap = { 'Files & Folders': 'folder1', 'Door Type': 'folder2', 'Operation Type': 'folder3' };
    setModal('renamefolder'); setModalMode(labelMap[label] || label); setModalValue(currentName); setModalDest(currentName); setModalError('');
  }
  function openDeleteFolder(label, currentName) {
    const labelMap = { 'Files & Folders': 'folder1', 'Door Type': 'folder2', 'Operation Type': 'folder3' };
    setModal('deletefolder'); setModalMode(labelMap[label] || label); setModalValue(currentName); setModalError('');
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Drawing Library"
        subtitle="Manage door drawing files — upload, rename, move, and delete files and folders"
        backTo="/settings"
        backLabel="Settings"
        sticky
      />

      <main className="max-w-6xl mx-auto px-6 py-8">

        {/* ── WHERE AM I? — current location display ──────────────────────── */}
        {folder1 && (
          <div style={{ background: '#000', color: '#fff', borderRadius: '6px', padding: '10px 16px', marginBottom: '12px', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#86efac' }}>📍</span>
            <span>Uploading to:</span>
            <span style={{ background: '#1a1a1a', padding: '4px 10px', borderRadius: '4px' }}>{fp || '/'}</span>
            {!folder2 && <span style={{ color: '#fca5a5', fontSize: '11px' }}>← select subfolders to narrow upload location</span>}
          </div>
        )}

        {/* ── Folder navigation bar ──────────────────────────────────────── */}
        <div className="bg-gray-300 rounded-lg shadow p-4 mb-4" style={{ border: '2px solid #000' }}>

          {/* Root-level actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#333' }}>FOLDER PATH — click the dropdowns to navigate, then upload below</div>
            <button onClick={openMkdirRoot}
              style={{ padding: '7px 14px', background: '#fff', border: '2px solid #000', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
              + New Top-Level Folder
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <FolderRow
              label="Files & Folders"
              value={folder1}
              options={f1Opts}
              onChange={v => { setFolder1(v); setFolder2(''); setFolder3(''); }}
              onMkdir={openMkdirAt}
              onRename={openRenameFolder}
              onDelete={openDeleteFolder}
              showActions={true}
            />
            {f2Opts.length > 0 && (
              <FolderRow
                label="Door Type"
                value={folder2}
                options={f2Opts}
                onChange={v => setFolder2(v)}
                onMkdir={openMkdirAt}
                onRename={openRenameFolder}
                onDelete={openDeleteFolder}
                showActions={true}
              />
            )}
            {f3Opts.length > 0 && (
              <FolderRow
                label="Operation Type"
                value={folder3}
                options={f3Opts}
                onChange={v => setFolder3(v)}
                onMkdir={openMkdirAt}
                onRename={openRenameFolder}
                onDelete={openDeleteFolder}
                showActions={true}
              />
            )}
          </div>
        </div>

        {/* ── Two-column layout ──────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

          {/* ── LEFT: Upload ──────────────────────────────────────────────── */}
          <div className="bg-gray-300 rounded-lg shadow p-4" style={{ border: '2px solid #000' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#333', marginBottom: '10px', textTransform: 'uppercase' }}>📤 Upload to current folder</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
              <input type="text" placeholder="File name (no extension)" value={model} onChange={e => setModel(e.target.value)}
                style={{ padding: '8px 10px', border: '2px solid #000', borderRadius: '6px', fontSize: '13px', width: '160px' }} />
              <input ref={fileInputRef} id="file-upload" type="file" accept=".dxf,.svg" style={{ display: 'none' }} onChange={handleFileChange} />
              <button onClick={() => document.getElementById('file-upload').click()}
                style={{ padding: '8px 12px', background: '#e5e5e5', border: '2px solid #000', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                {selectedFile ? selectedFile.name.substring(0, 22) : 'Choose File'}
              </button>
              <button onClick={handleUpload} disabled={uploading || !selectedFile || !model.trim() || !folder1}
                style={{ padding: '8px 14px', background: (!selectedFile || !model.trim() || !folder1) ? '#ccc' : '#28a745', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: (!selectedFile || !model.trim() || !folder1) ? 'default' : 'pointer' }}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
            <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
              onClick={() => document.getElementById('file-upload').click()}
              style={{ border: `2px dashed ${dragOver ? '#0070f3' : '#888'}`, borderRadius: '6px', padding: '20px 10px', textAlign: 'center', background: dragOver ? '#e8f4ff' : '#f0f0f0', cursor: 'pointer', fontSize: '13px', color: '#555' }}>
              or drop DXF / SVG file here
            </div>
            {!folder1 && (
              <div style={{ marginTop: '8px', padding: '8px 10px', background: '#fef9c3', border: '2px solid #000', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: '#713f12' }}>
                ⚠️ Select a folder above using the dropdowns before uploading
              </div>
            )}
            {uploadResult && (
              <div style={{ marginTop: '8px', padding: '8px 10px', background: '#86efac', border: '2px solid #000', borderRadius: '6px', fontSize: '11px', fontFamily: 'monospace', fontWeight: '600' }}>
                {uploadResult}
              </div>
            )}
            {uploadError && (
              <div style={{ marginTop: '8px', padding: '8px 10px', background: '#fca5a5', border: '2px solid #000', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: '#991b1b' }}>
                ❌ {uploadError}
              </div>
            )}
          </div>

          {/* ── RIGHT: Preview + file list ───────────────────────────────── */}
          <div className="bg-gray-300 rounded-lg shadow" style={{ border: '2px solid #000', overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', borderBottom: '2px solid #000', fontSize: '11px', fontWeight: '700', color: '#333', textTransform: 'uppercase', background: '#e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              👁 Preview
              {svg && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button onClick={() => setZoom(z => Math.max(10, z - 10))} style={{ background: '#fff', border: '2px solid #000', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', cursor: 'pointer', fontWeight: '700', lineHeight: '1' }}>−</button>
                  <span style={{ fontSize: '11px', fontWeight: '600', minWidth: '32px', textAlign: 'center' }}>{zoom}%</span>
                  <button onClick={() => setZoom(z => Math.min(300, z + 10))} style={{ background: '#fff', border: '2px solid #000', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', cursor: 'pointer', fontWeight: '700', lineHeight: '1' }}>+</button>
                </div>
              )}
            </div>
            <div style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', overflow: 'auto' }}>
              {svg ? (
                <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'center center', transition: 'transform 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center' }} dangerouslySetInnerHTML={{ __html: svg }} />
              ) : (
                <span style={{ color: '#888', fontSize: '13px' }}>Click a file to preview</span>
              )}
            </div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#333', padding: '8px 14px', borderTop: '2px solid #000', background: '#e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📄 Files in this folder {loading && '(...)'}</span>
              {selected && (
                <span style={{ fontSize: '11px', color: '#166534', background: '#bbf7d0', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>
                  {selected}
                </span>
              )}
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {files.length === 0 && !loading && (
                <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '12px' }}>
                  {folder1 ? 'No files in this folder' : 'Select a folder above to see files'}
                </div>
              )}
              {files.map(f => (
                <div key={f.id}
                  style={{
                    padding: '8px 14px', borderBottom: '1px solid #ddd',
                    background: selected === f.filename ? '#dbeafe' : 'transparent',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: 'pointer',
                  }}>
                  <span onClick={() => loadPreview(f.filename)} style={{ fontSize: '12px', color: '#333', flex: '1' }}>{f.filename}</span>
                  <div style={{ display: 'flex', gap: '4px', marginLeft: '8px', flexShrink: 0 }}>
                    <button onClick={() => loadPreview(f.filename)} title="Preview" style={{ background: '#fff', border: '2px solid #000', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: '700' }}>👁</button>
                    <button onClick={() => { setSelected(f.filename); loadPreview(f.filename); openRenameFile(f.filename); }} title="Rename" style={{ background: '#fff', border: '2px solid #000', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: '700' }}>✏️</button>
                    <button onClick={() => { setSelected(f.filename); loadPreview(f.filename); openMoveFile(f.filename); }} title="Move" style={{ background: '#fff', border: '2px solid #000', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: '700' }}>↗️</button>
                    <button onClick={() => { setSelected(f.filename); loadPreview(f.filename); confirmDeleteFile(f.filename); }} title="Delete" style={{ background: '#fff', border: '2px solid #dc2626', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', color: '#dc2626', fontWeight: '700' }}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {/* Mkdir */}
      {modal === 'mkdir' && (
        <Modal title={modalMode === 'root' ? 'New Top-Level Folder' : `New Subfolder in ${modalMode === 'folder1' ? folder1 : modalMode === 'folder2' ? folder2 : folder3}`} onClose={() => setModal(null)}>
          <Field label="Folder name" value={modalValue} onChange={e => setModalValue(e.target.value)} placeholder="e.g. Fanlight" />
          {modalError && <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>❌ {modalError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={() => doMkdirAt(modalMode)} disabled={!modalValue.trim() || modalLoading}>
              {modalLoading ? '...' : 'Create Folder'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Rename folder */}
      {modal === 'renamefolder' && (
        <Modal title={`Rename Folder — ${modalValue}`} onClose={() => setModal(null)}>
          <Field label="New folder name" value={modalDest} onChange={e => setModalDest(e.target.value)} />
          {modalError && <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>❌ {modalError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={doRenameFolder} disabled={!modalDest.trim() || modalLoading}>
              {modalLoading ? '...' : 'Rename Folder'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Delete folder */}
      {modal === 'deletefolder' && (
        <Modal title={`Delete Folder — ${modalValue}?`} onClose={() => setModal(null)}>
          <p style={{ fontSize: '13px', color: '#333', marginBottom: '8px', fontWeight: '600' }}>
            This will delete the folder <strong>{modalValue}</strong>.
          </p>
          <p style={{ fontSize: '12px', color: '#dc2626', marginBottom: '16px', fontWeight: '600' }}>
            ⚠️ The folder must be empty. If it has files, delete those first.
          </p>
          {modalError && <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>❌ {modalError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={doDeleteFolder} disabled={modalLoading}>
              {modalLoading ? '...' : 'Delete Folder'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Rename file */}
      {modal === 'renamefile' && (
        <Modal title="Rename File" onClose={() => setModal(null)}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Current: <strong>{selected}</strong></div>
          <Field label="New file name (without .svg)" value={modalValue} onChange={e => setModalValue(e.target.value)} />
          {modalError && <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>❌ {modalError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={doRenameFile} disabled={!modalValue.trim() || modalLoading}>
              {modalLoading ? '...' : 'Rename'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Move file */}
      {modal === 'movefile' && (
        <Modal title="Move File" onClose={() => setModal(null)}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', fontWeight: '600' }}>
            Moving: <strong>{modalValue}</strong>
          </div>
          <Field label="Destination folder" value={modalDest} onChange={e => setModalDest(e.target.value)} type="select">
            <option value="">-- Select folder --</option>
            {flattenTree(tree).map(p => <option key={p} value={p}>{p}</option>)}
          </Field>
          {modalError && <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>❌ {modalError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="primary" onClick={doMoveFile} disabled={!modalDest.trim() || modalLoading}>
              {modalLoading ? '...' : 'Move'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Delete file */}
      {modal === 'deletefile' && (
        <Modal title="Delete File?" onClose={() => setModal(null)}>
          <p style={{ fontSize: '13px', color: '#333', marginBottom: '16px', fontWeight: '600' }}>
            Permanently delete <strong>{modalValue}</strong>? This cannot be undone.
          </p>
          {modalError && <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>❌ {modalError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Btn onClick={() => setModal(null)}>Cancel</Btn>
            <Btn variant="danger" onClick={doDeleteFile} disabled={modalLoading}>
              {modalLoading ? '...' : 'Delete'}
            </Btn>
          </div>
        </Modal>
      )}

    </div>
  );
}